import {
  type PhilatelicItem,
  type SingleStamp,
  type StampFromSet,
  type PhilatelicSet,
  type Block,
  type Booklet,
  type SeTenants,
  type StampColor,
  type Perforation,
  type Size,
  type CatalogIds,
  PhilatelicItemType,
  formatZhihao,
} from '@briefmarken_katalog/types';
import { translate, type TranslationKey, type Locale } from '$lib/i18n';
import { formatDateDisplay } from '$lib/utils/utils';
import { formatSecurityPerforation } from '$lib/utils/securityPerforation';

/** A single attribute rendered as a chip in the catalog detail dialog. */
export type Fact = { label: string; value: string };

/**
 * Everything the catalog detail dialog can show. A StampFromSet is not a standalone
 * catalog item — it carries no own issue date, which the dialog takes from its set.
 */
export type CatalogDetailItem = PhilatelicItem | StampFromSet;

const TYPE_KEYS: Record<PhilatelicItemType, TranslationKey> = {
  [PhilatelicItemType.SingleStamp]:  'search.singleStamp',
  [PhilatelicItemType.Set]:          'menu.set',
  [PhilatelicItemType.StampFromSet]: 'search.stampFromSet',
  [PhilatelicItemType.Block]:        'menu.block',
  [PhilatelicItemType.Booklet]:      'menu.booklet',
  [PhilatelicItemType.SeTenants]:    'menu.seTenants',
};

export function itemTypeKey(type: PhilatelicItemType): TranslationKey {
  return TYPE_KEYS[type];
}

function formatNumber(value: number, loc: Locale): string {
  return new Intl.NumberFormat(loc).format(value);
}

function formatPerforation(p: Perforation, loc: Locale): string {
  const typeLabel = translate(`fields.perforationTypeLabels.${p.type}` as TranslationKey);
  if (p.type === 'Imperforated') return typeLabel;
  return `${formatNumber(p.horizontal, loc)} × ${formatNumber(p.vertical, loc)} · ${typeLabel}`;
}

function formatColor(c: StampColor): string {
  if ('isMultiColored' in c && c.isMultiColored) return translate('fields.color.multiColored');
  if ('secondary' in c && c.secondary) return `${c.primary} / ${c.secondary}`;
  return 'primary' in c ? c.primary : '';
}

function formatSize(s: Size, loc: Locale): string {
  return `${formatNumber(s.width, loc)} × ${formatNumber(s.height, loc)} ${translate('fields.mm')}`;
}

function catalogIdFacts(ids: CatalogIds | undefined): Fact[] {
  if (!ids) return [];
  return Object.entries(ids)
    .filter(([, id]) => !!id)
    .map(([name, id]) => ({ label: name.replace(/_/g, ' '), value: id as string }));
}

/**
 * Builds the chip list for the catalog detail dialog. Attributes that are unset are
 * omitted entirely — the dialog shows only what the item actually carries.
 *
 * Title, description, issue date and the contained stamps are rendered separately by
 * the dialog and are therefore not part of the result.
 */
export function buildFacts(item: CatalogDetailItem, loc: Locale): Fact[] {
  const facts: Fact[] = [];
  const add = (labelKey: TranslationKey, value: string | undefined) => {
    if (value) facts.push({ label: translate(labelKey), value });
  };

  // The Zhihao comes first — for PRC material it is the primary identifier.
  if (item.zhihao) {
    add('zhihao.button', item.zhihao.raw || formatZhihao(item.zhihao));
  }

  if (item.expiry_date) {
    add('fields.expiryDate', formatDateDisplay(new Date(item.expiry_date), loc));
  }

  switch (item.type) {
    case PhilatelicItemType.SingleStamp:
    case PhilatelicItemType.StampFromSet: {
      const stamp = item as SingleStamp | StampFromSet;
      if (stamp.face_value) add('fields.faceValue', `${formatNumber(stamp.face_value.value, loc)} ${stamp.face_value.unit}`);
      if (stamp.perforation) add('fields.perforation', formatPerforation(stamp.perforation, loc));
      // Its own chip rather than an appendix to the perforation — it carries six attributes.
      if (stamp.perforation?.security) {
        add('fields.securityPerforation.label', formatSecurityPerforation(stamp.perforation.security, loc));
      }
      if (stamp.with_gum) add('fields.gum.label', translate(`fields.gum.${stamp.with_gum}` as TranslationKey));
      if (stamp.color) add('fields.color.label', formatColor(stamp.color));
      if (stamp.print_run) add('fields.printRun', formatNumber(stamp.print_run, loc));
      break;
    }
    case PhilatelicItemType.Set: {
      const set = item as PhilatelicSet;
      add('editor.stamps', String(set.stamps?.length ?? 0));
      break;
    }
    case PhilatelicItemType.Block:
    case PhilatelicItemType.Booklet: {
      const collection = item as Block | Booklet;
      add('editor.stamps', String(collection.stamps?.length ?? 0));
      if (collection.size) add('fields.size', formatSize(collection.size, loc));
      if (collection.print_run) add('fields.printRun', formatNumber(collection.print_run, loc));
      break;
    }
    case PhilatelicItemType.SeTenants: {
      const seTenants = item as SeTenants;
      add('editor.stamps', String(seTenants.stamps?.length ?? 0));
      if (seTenants.print_run) add('fields.printRun', formatNumber(seTenants.print_run, loc));
      break;
    }
  }

  facts.push(...catalogIdFacts(item.catalog_ids));
  return facts;
}
